import { forwardRef, type HTMLAttributes, type ReactElement, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import type { CardHeadingLevel } from './Card'
import './empty-state.css'

/** default fills a region, such as a page or a camera grid with nothing in it; compact sits inside a list or a card. */
export type EmptyStateSize = 'default' | 'compact'

/** default sits on the light canvas; inverse sits on media, such as a camera's dark video surface with no picture. */
export type EmptyStateTone = 'default' | 'inverse'

export type EmptyStateProps = Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
  /** Names what is missing, such as No cameras yet. Required: an empty state never leaves people guessing. */
  title: ReactNode
  /** What appears here once it isn't empty, or why it is empty. One or two short sentences. */
  description?: ReactNode
  /** A Lucide icon that echoes what is missing. Decorative: the title says it. */
  icon?: ReactElement
  /** The next task, usually one Button. Leave it out when the same action already leads the view. */
  action?: ReactNode
  /** The heading level for the title, so the page outline stays meaningful. */
  headingLevel?: CardHeadingLevel
  size?: EmptyStateSize
  /** inverse reads on a dark media surface: white title, muted light description, a dark disc. */
  tone?: EmptyStateTone
}

/** A named, honest placeholder for a region with nothing in it yet, leading with the next task. */
export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { title, description, icon, action, headingLevel = 2, size = 'default', tone = 'default', className, ...props },
  ref,
) {
  const Heading = `h${headingLevel}` as const
  return (
    <div ref={ref} className={cn('play-empty-state', size === 'compact' && 'play-empty-state--compact', tone === 'inverse' && 'play-empty-state--inverse', className)} {...props}>
      {icon && <span className="play-empty-state__icon" aria-hidden="true">{icon}</span>}
      <Heading className="play-empty-state__title">{title}</Heading>
      {description && <p className="play-empty-state__description">{description}</p>}
      {action && <div className="play-empty-state__actions">{action}</div>}
    </div>
  )
})
